import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type LanguageCode = string;
type TranslationData = Record<string, any>;

interface I18nContextType {
  language: LanguageCode;
  setLanguage: (lang: LanguageCode) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  isLoading: boolean;
}

const I18nContext = createContext<I18nContextType>({
  language: 'en',
  setLanguage: () => {},
  t: (key) => key,
  isLoading: true,
});

// 캐시 버전 - 업데이트 시 변경
const CACHE_VERSION = '20260816v1';

const translationCache: Record<string, TranslationData> = {};

// WIA 언어 모달(127개)이 내보내는 코드 중 로케일 파일명과 다른 것들.
// 예: 모달은 'zh-Hans'를 주는데 파일은 zh-CN.json, 'iw'(구 히브리어 코드)는 he.json.
const LOCALE_ALIASES: Record<string, string> = {
  zh: 'zh-CN',
  'zh-Hans': 'zh-CN',
  'zh-Hant': 'zh-TW',
  'zh-HK': 'zh-TW',
  iw: 'he',   // 히브리어 구 ISO 코드
  nb: 'no',   // 보크몰 → no.json
  fil: 'tl',  // 필리핀어 → tl.json
  in: 'id',   // 인도네시아어 구 코드
  ji: 'yi',   // 이디시어 구 코드
};

// 지역변형(es-AR, en-GB, ar-SA …)은 전용 파일이 없으므로 기본 언어로 내려간다.
// 시도 순서: 별칭 → 원본 → 기본어 별칭 → 기본어 → 영어
const localeCandidates = (code: string): string[] => {
  const out: string[] = [];
  const push = (c?: string) => {
    if (c && !out.includes(c)) out.push(c);
  };
  push(LOCALE_ALIASES[code]);
  push(code);
  const base = code.split('-')[0];
  if (base !== code) {
    push(LOCALE_ALIASES[base]);
    push(base);
  }
  push('en');
  return out;
};

// 아랍어·히브리어·페르시아어·우르두어 등은 오른쪽에서 왼쪽으로 읽는다.
const RTL_BASE = new Set(['ar', 'he', 'iw', 'fa', 'ur', 'ps', 'sd', 'ug', 'yi', 'dv', 'ku', 'ckb']);
const applyDirection = (code: string) => {
  const dir = RTL_BASE.has(code.split('-')[0]) ? 'rtl' : 'ltr';
  document.documentElement.dir = dir;
  document.documentElement.lang = code;
};

const getNestedValue = (obj: TranslationData, path: string): string => {
  const keys = path.split('.');
  let value: any = obj;
  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      return path;
    }
  }
  return typeof value === 'string' ? value : path;
};

const interpolate = (str: string, params?: Record<string, string | number>): string => {
  if (!params) return str;
  return str.replace(/\{\{(\w+)\}\}/g, (_, key) => params[key]?.toString() ?? `{{${key}}}`);
};

interface I18nProviderProps {
  children: ReactNode;
  defaultLanguage?: LanguageCode;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ children, defaultLanguage = 'en' }) => {
  const [language, setLanguageState] = useState<LanguageCode>(() => {
    // 1순위: localStorage 저장값.
    // ★'wia-nav-lang'은 공용 언어 모달도 쓰는 키인데, 모달이 초기화하면서 자기 기본값('en')을
    //   비동기로 써버릴 때가 있다. 그래서 이 앱이 직접 쓰는 키를 먼저 보고, 모달 키는
    //   "다른 WIA 사이트에서 고른 언어 물려받기" 용도로만 마지막에 본다.
    const saved =
      localStorage.getItem('wiabooks_language') ||
      localStorage.getItem('wia-language') ||
      localStorage.getItem('wia-nav-lang');
    if (saved) {
      // 자동감지 결과가 모달의 기본값에 덮이지 않도록 우리 키에도 즉시 고정한다.
      localStorage.setItem('wiabooks_language', saved);
      return saved;
    }
    // 2순위: 브라우저 언어 자동 감지 (ko-KR → ko, zh-CN → zh-CN 등)
    const browserLang = navigator.language || (navigator as any).userLanguage || '';
    const lang = browserLang.toLowerCase();
    const persist = (code: string) => {
      // ★렌더 이후(useEffect)까지 미루면 모달의 비동기 초기화가 먼저 'en'을 써버린다.
      localStorage.setItem('wiabooks_language', code);
      return code;
    };
    if (lang.startsWith('ko')) return persist('ko');
    if (lang.startsWith('zh-tw') || lang.startsWith('zh-hant')) return persist('zh-TW');
    if (lang.startsWith('zh')) return persist('zh-CN');
    if (lang.startsWith('ja')) return persist('ja');
    if (lang.startsWith('es')) return persist('es');
    if (lang.startsWith('fr')) return persist('fr');
    if (lang.startsWith('de')) return persist('de');
    if (lang.startsWith('pt')) return persist('pt');
    if (lang.startsWith('ru')) return persist('ru');
    if (lang.startsWith('ar')) return persist('ar');
    if (lang.startsWith('hi')) return persist('hi');
    if (lang.startsWith('id')) return persist('id');
    if (lang.startsWith('th')) return persist('th');
    if (lang.startsWith('vi')) return persist('vi');
    if (lang.startsWith('tr')) return persist('tr');
    if (lang.startsWith('it')) return persist('it');
    if (lang.startsWith('nl')) return persist('nl');
    if (lang.startsWith('pl')) return persist('pl');
    if (lang.startsWith('uk')) return persist('uk');
    // 3순위: 지역만 떼고 언어 부분을 그대로 쓴다(2글자든 3글자든).
    // ★예전엔 lang.slice(0, 2)로 잘랐는데, 3글자 코드가 엉뚱한 언어로 붙었다 —
    //   ceb(세부아노)→'ce'(체첸어), haw(하와이어)→'ha'(하우사어). 영어 폴백보다 나쁘다.
    //   못 찾으면 localeCandidates가 영어로 안전하게 내려준다.
    return persist(lang.split('-')[0] || defaultLanguage);
  });
  const [translations, setTranslations] = useState<TranslationData>({});
  const [isLoading, setIsLoading] = useState(true);

  const setLanguage = (lang: LanguageCode) => {
    setLanguageState(lang);
    localStorage.setItem('wia-language', lang);
    localStorage.setItem('wiabooks_language', lang);
    localStorage.setItem('wia-nav-lang', lang);
  };

  // 기존 WIA 모달의 언어 변경 이벤트 리스닝
  useEffect(() => {
    const handleLanguageChange = (e: CustomEvent) => {
      const newLang = e.detail?.language;
      if (newLang && newLang !== language) {
        console.log('🌍 Language changed via WIA Modal:', newLang);
        setLanguageState(newLang);
        localStorage.setItem('wia-language', newLang);
        localStorage.setItem('wiabooks_language', newLang);
        localStorage.setItem('wia-nav-lang', newLang);
        // 캐시 무효화
        delete translationCache[newLang];
      }
    };

    window.addEventListener('wia-language-changed', handleLanguageChange as EventListener);
    return () => {
      window.removeEventListener('wia-language-changed', handleLanguageChange as EventListener);
    };
  }, [language]);

  // 문서 방향(RTL/LTR)·lang 속성을 맞추고, 언어 모달이 같은 값을 보도록 저장소를 동기화한다.
  // (첫 방문 시 앱은 브라우저 언어를 자동감지하는데 저장은 안 해서, 모달만 엉뚱한 언어를
  //  선택된 것으로 표시하던 불일치가 있었다.)
  useEffect(() => {
    applyDirection(language);
    localStorage.setItem('wia-language', language);
    localStorage.setItem('wiabooks_language', language);
    localStorage.setItem('wia-nav-lang', language);
  }, [language]);

  useEffect(() => {
    const loadTranslations = async () => {
      setIsLoading(true);
      
      // 캐시 확인
      if (translationCache[language]) {
        setTranslations(translationCache[language]);
        setIsLoading(false);
        return;
      }
      
      try {
        let data: TranslationData | null = null;

        // 별칭·지역변형을 순서대로 시도한다(마지막 후보는 항상 영어).
        // ★없는 로케일은 404가 아니라 200 + index.html로 온다(.htaccess의 SPA 폴백 규칙).
        //   그래서 response.ok만 보면 안 되고 반드시 JSON인지 확인해야 한다.
        for (const candidate of localeCandidates(language)) {
          const url = `/wia-epub-bookshelf/locales/${candidate}.json?v=${CACHE_VERSION}`;
          try {
            const response = await fetch(url, { cache: 'no-store' });
            if (!response.ok) continue;
            if (!(response.headers.get('content-type') || '').includes('json')) continue;
            const parsed = await response.json();
            if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) continue;
            data = parsed;
            if (candidate === language) {
              console.log(`✅ Loaded translations for: ${language} (v${CACHE_VERSION})`);
            } else {
              console.log(`✅ Loaded translations for: ${language} → ${candidate}.json`);
            }
            break;
          } catch {
            continue;
          }
        }
        if (!data) throw new Error(`no locale file for ${language}`);

        translationCache[language] = data;
        setTranslations(data);
      } catch (error) {
        console.error('Failed to load translations:', error);
        // 에러 시 기본 영어 import
        try {
          const fallback = (await import('../locales/en.json')).default;
          setTranslations(fallback);
        } catch {
          setTranslations({});
        }
      }
      
      setIsLoading(false);
    };
    
    loadTranslations();
  }, [language]);

  const t = (key: string, params?: Record<string, string | number>): string => {
    const value = getNestedValue(translations, key);
    return interpolate(value, params);
  };

  return (
    <I18nContext.Provider value={{ language, setLanguage, t, isLoading }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => useContext(I18nContext);

export const LANGUAGES = [
  { code: 'en', name: 'English', native: 'English' },
  { code: 'ko', name: 'Korean', native: '한국어' },
];

export default I18nContext;
